/**
 * UnifiedAnalyticsPage — one row per (app, platform) joining cumulative
 * downloads + average rating + latest GA4 metrics for matching web
 * properties. DataGrid handles sort / filter / search / CSV export.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';

import { unifiedApi, refreshCache } from '../services/mrdsApi';
import {
  PageHeader,
  SectionCard,
  EmptyState,
  PlatformChip,
  DataTable,
} from '../components/ui';

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('tr-TR'));
const fmtRating = (n) => (n == null ? '—' : Number(n).toFixed(2));

export default function UnifiedAnalyticsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    unifiedApi
      .getAnalytics()
      .then((r) => {
        if (cancelled) return;
        setRows(r);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(err);
        setError('Birleşik analitik yüklenirken hata oluştu.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const gridRows = useMemo(
    () =>
      rows.map((r, i) => ({
        id: `${r.app_name}-${r.platform}-${i}`,
        ...r,
      })),
    [rows],
  );

  const columns = [
    {
      field: 'app_name',
      headerName: 'App',
      flex: 1.4,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'platform',
      headerName: 'Platform',
      flex: 0.7,
      minWidth: 110,
      renderCell: ({ value }) => <PlatformChip platform={value} />,
    },
    {
      field: 'app_extid',
      headerName: 'Store ID',
      flex: 1,
      minWidth: 140,
      renderCell: ({ value }) =>
        value ? (
          <Chip label={value} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
        ) : '—',
    },
    {
      field: 'total_downloads',
      headerName: 'Downloads',
      type: 'number',
      flex: 0.9,
      minWidth: 120,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'avg_rating',
      headerName: 'Avg ★',
      type: 'number',
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) =>
        value == null ? '—' : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <StarIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
            <Typography variant="body2">{fmtRating(value)}</Typography>
          </Stack>
        ),
    },
    {
      field: 'latest_rating',
      headerName: 'Latest ★',
      type: 'number',
      flex: 0.7,
      minWidth: 100,
      valueFormatter: ({ value }) => fmtRating(value),
    },
    {
      field: 'ga4_active_users',
      headerName: 'GA4 active',
      type: 'number',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'ga4_new_users',
      headerName: 'GA4 new',
      type: 'number',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: ({ value }) => fmt(value),
    },
    {
      field: 'ga4_event_counts',
      headerName: 'GA4 events',
      type: 'number',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: ({ value }) => fmt(value),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Unified Analytics"
        subtitle="One row per (app, platform). Cumulative downloads, ratings and the latest GA4 snapshot, all in one place."
        rightSlot={
          <MuiTooltip title="Refresh">
            <IconButton
              onClick={() => {
                refreshCache();
                setReloadKey((k) => k + 1);
              }}
            >
              <RefreshIcon />
            </IconButton>
          </MuiTooltip>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard
        title={`${gridRows.length} row(s)`}
        description="Use the toolbar to search, filter, hide columns or export to CSV."
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : gridRows.length === 0 ? (
          <EmptyState title="No matches." dense />
        ) : (
          <DataTable
            rows={gridRows}
            columns={columns}
            getRowId={(r) => r.id}
            height={620}
            initialState={{
              sorting: { sortModel: [{ field: 'total_downloads', sort: 'desc' }] },
            }}
          />
        )}
      </SectionCard>
    </Container>
  );
}
